import { RotateCcw } from "lucide-react";
import dayjs from "dayjs";
import { prettyLoc } from "@/lib/format";

const pill = (active) =>
  `shrink-0 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${
    active ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-400"
  }`;

const dateCls =
  "shrink-0 bg-white border border-slate-200 text-xs font-semibold text-slate-700 rounded-full px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]";

const Divider = () => <span className="shrink-0 w-px h-5 bg-slate-200 mx-1" />;

export const FilterBar = ({ meta, single = false, locations, setLocations, fy, setFy, dateFrom, setDateFrom, dateTo, setDateTo, coinsMode, setCoinsMode }) => {
  const allLocs = meta?.locations || [];
  const toggleLoc = (l) => {
    const next = locations.includes(l) ? locations.filter((x) => x !== l) : [...locations, l];
    setLocations(next.length === allLocs.length ? [] : next);
  };
  const reset = () => {
    setLocations([]);
    setFy("all");
    setDateFrom("");
    setDateTo("");
    setCoinsMode("with");
  };
  const now = dayjs();
  const presets = [
    { id: "current-month", label: "Current Month", from: now.startOf("month").format("YYYY-MM-DD"), to: now.format("YYYY-MM-DD") },
    { id: "last-month", label: "Last Month", from: now.subtract(1, "month").startOf("month").format("YYYY-MM-DD"), to: now.subtract(1, "month").endOf("month").format("YYYY-MM-DD") },
  ];
  return (
    <div data-testid="filter-bar" className="sticky top-16 z-40 bg-white/85 backdrop-blur-xl border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-2.5 space-y-2">
        <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
          {!single && allLocs.length !== 1 && (
            <>
              <button data-testid="filter-location-all" className={pill(locations.length === 0)} onClick={() => setLocations([])}>
                All Locations
              </button>
              {allLocs.map((l) => (
                <button
                  key={l}
                  data-testid={`filter-location-${l.split(" ")[0].toLowerCase()}`}
                  className={pill(locations.includes(l))}
                  onClick={() => toggleLoc(l)}
                >
                  {prettyLoc(l)}
                </button>
              ))}
              <Divider />
            </>
          )}
          {[
            { value: "with", label: "With Coins" },
            { value: "without", label: "Without Coins" },
            { value: "only", label: "Only Coins" },
          ].map((c) => (
            <button
              key={c.value}
              data-testid={`filter-coins-${c.value}`}
              className={pill(coinsMode === c.value)}
              onClick={() => setCoinsMode(c.value)}
            >
              {c.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
          <button data-testid="filter-fy-all" className={pill(fy === "all")} onClick={() => setFy("all")}>
            All FY
          </button>
          {(meta?.fys || []).map((f) => (
            <button key={f} data-testid={`filter-fy-${f.split(" ")[1]}`} className={pill(fy === f)} onClick={() => setFy(f)}>
              {f}
            </button>
          ))}
          <Divider />
          {presets.map((p) => (
            <button
              key={p.id}
              data-testid={`filter-preset-${p.id}`}
              className={pill(dateFrom === p.from && dateTo === p.to)}
              onClick={() => {
                setDateFrom(p.from);
                setDateTo(p.to);
              }}
            >
              {p.label}
            </button>
          ))}
          <Divider />
          <input
            type="date"
            data-testid="filter-date-from"
            value={dateFrom}
            min={meta?.date_from || undefined}
            max={meta?.date_to || undefined}
            onChange={(e) => setDateFrom(e.target.value)}
            className={dateCls}
          />
          <span className="shrink-0 text-[10px] uppercase tracking-widest text-slate-400">to</span>
          <input
            type="date"
            data-testid="filter-date-to"
            value={dateTo}
            min={meta?.date_from || undefined}
            max={meta?.date_to || undefined}
            onChange={(e) => setDateTo(e.target.value)}
            className={dateCls}
          />
          <button
            onClick={reset}
            data-testid="filter-reset"
            className="shrink-0 flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-900 px-2 py-1.5 transition-colors"
          >
            <RotateCcw size={12} /> Reset
          </button>
        </div>
      </div>
    </div>
  );
};
